'use client';

import { Alert } from '@heroui/alert';

import {
  CheckCircle,
  DollarSign,
  Users,
  Wallet,
  ArrowUpDown,
  BanknoteIcon,
  Shield,
  CreditCard,
  LockKeyhole,
  Zap,
} from 'lucide-react';

import Download from '@/components/common/download';
import ContactUsCTA from '@/components/common/ContactCTA';
import { Feature } from '@/components/common/feature-variants';
export default function Page() {
  const sections = [
    {
      title: 'For Customers',
      description:
        'Access your digital bank account or mobile wallet through our trusted network of agents.',
      processes: [
        {
          title: 'Accessing Your Account',
          description:
            'Visit a local PayStop Super Agent. The Super Agent will securely connect to your account provider through a secured connection, allowing you to perform transactions like deposits, withdrawals, and transfers.',
          icon: BanknoteIcon,
        },
        {
          title: 'Depositing Money',
          description:
            'Hand over your cash to the Super Agent who will process the deposit into your digital bank account or mobile wallet. The agent will authenticate and authorise the transaction through a secure process, ensuring that your account balance is updated immediately.',
          icon: DollarSign,
        },
        {
          title: 'Withdrawing Money',
          description:
            'Visit a local PayStop Super Agent for withdrawals. The Super Agent will process the withdrawal request through the secured connection to your account provider, and you will receive the cash once the transaction is confirmed with secure authentication.',
          icon: Wallet,
        },
        {
          title: 'Money Transfers',
          description:
            "Transfer money from your digital bank account or mobile wallet to another account—whether it's another PayStop account or a third-party provider. The transfer process is completed securely through your account provider.",
          icon: ArrowUpDown,
        },
        {
          title: 'No Need to Sign Up',
          description:
            "You don't need to create a separate PayStop account. Instead, you access your existing account provider's services via the PayStop network. This simplifies the process while ensuring your funds are securely handled.",
          icon: CheckCircle,
        },
      ],
    },
    {
      title: 'For Super Agents',
      description: 'The Super Agent is the first and primary user within the PayStop network.',
      processes: [
        {
          title: 'Sign Up as a Super Agent',
          description:
            'Download the PayStop app, register your business, and fund your Super Agent account through a commercial bank. After registration, you can begin providing financial services to your community.',
          icon: CreditCard,
        },
        {
          title: 'Facilitate Transactions',
          description:
            "Handle all customer transactions, including deposits, withdrawals, and money transfers. Using the PayStop platform, you securely connect to the customer's account provider and complete the transaction in real-time.",
          icon: DollarSign,
        },
        {
          title: 'Earn Rewards',
          description:
            "For every transaction made—whether it's a deposit, withdrawal, or transfer—you'll earn a reward based on a percentage of the transaction fee. The fee is shared with any Sub-Agents within your network.",
          icon: Zap,
        },
        {
          title: 'Expand Your Network',
          description:
            'Invite Sub-Agents and Agent Admins to join your network and assist with managing customer transactions. Sub-Agents handle the same services you provide and earn rewards for every transaction they complete.',
          icon: Users,
        },
      ],
    },
    {
      title: 'For Agent Admins',
      description: 'The Agent Admin is a trusted user assigned by the Super Agent.',
      processes: [
        {
          title: 'Access Super Agent Account',
          description:
            "As an Agent Admin, you have access to the Super Agent's account. You can help manage transactions, verify customer requests, and ensure the smooth operation of the agent network.",
          icon: LockKeyhole,
        },
        {
          title: 'Facilitate Transactions',
          description:
            "Support customers by facilitating deposits and withdrawals on behalf of the Super Agent. You will securely access the customer's account provider to process these transactions.",
          icon: DollarSign,
        },
        {
          title: 'Monitor Transactions',
          description:
            "Track transaction histories, approve or reject certain actions (as per the Super Agent's instructions), and manage day-to-day operations of the business.",
          icon: Shield,
        },
        {
          title: 'Manage Customer Requests',
          description:
            "Handle customer inquiries, assist with troubleshooting, and ensure the customers' needs are met swiftly.",
          icon: Users,
        },
      ],
    },
    {
      title: 'For Sub-Agents',
      description: 'Sub-Agents are invited by Super Agents to help provide on-the-ground services.',
      processes: [
        {
          title: 'Sign Up as a Sub-Agent',
          description:
            "Once invited by a Super Agent, you'll sign up and receive your credentials to begin working with PayStop.",
          icon: CreditCard,
        },
        {
          title: 'Facilitate Transactions',
          description:
            "Help facilitate deposits and withdrawals on behalf of customers. Using the PayStop platform, Sub-Agents securely connect to the customer's account provider via a secured connection.",
          icon: DollarSign,
        },
        {
          title: 'Earn Rewards',
          description:
            "As a Sub-Agent, you will earn a portion of the rewards for every transaction you facilitate—whether it's a deposit, withdrawal, or transfer.",
          icon: Zap,
        },
        {
          title: 'Provide Local Support',
          description:
            "Serve as the local point of contact for customers. You'll ensure that customers have a smooth experience with PayStop services.",
          icon: Users,
        },
      ],
    },
  ];

  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              How it Works
            </h1>
            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              At PayStop, we simplify access to financial services by connecting local businesses,
              agents, and customers in a seamless network. Through our platform, you can deposit,
              withdraw, and transfer funds securely without the need for complex procedures. Here's
              how it works for <strong>customers, Super Agents, Agent Admins,</strong> and
              <strong> Sub-Agents</strong>.
            </p>
          </div>
        </div>
      </section>
      {sections.map((section, sectionIndex) => (
        <section
          key={sectionIndex}
          className={`section-md ${sectionIndex % 2 === 0 ? 'bg-background' : 'bg-gray-50 dark:bg-background'}`}
        >
          <div className="container px-4 mx-auto max-w-7xl">
            <div className="text-center mb-14">
              <h2 className="mb-4 text-3xl font-bold tracking-tight">{section.title}</h2>
              <p className="max-w-3xl mx-auto text-lg text-foreground/70">{section.description}</p>
            </div>

            <div className="relative">
              {/* Process Connection Line */}
              <div className="absolute left-[2.45rem] top-0 h-full w-0.5 bg-primary/20 md:left-1/2 -ml-0.5" />

              <div className="space-y-12">
                {section.processes.map((process, index) => (
                  <div key={index} className="relative">
                    <div className="relative flex items-center md:justify-center">
                      <div className="relative flex items-center justify-center w-8 h-8 text-white rounded-full md:left-0 bg-primary left-5">
                        {index + 1}
                      </div>
                    </div>

                    <Feature
                      key={index}
                      description={process.description}
                      icon={<process.icon className="w-7 h-7 text-primary" />}
                      title={process.title}
                      variant="howItWorks"
                      index={index}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Security Measures Section */}
      <section className="section-md">
        <div className="container container-md">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight">Security Measures</h2>
            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              At PayStop, security is our top priority. We use industry-leading encryption and
              security protocols.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Alert
              hideIcon
              color="primary"
              description={
                <ul className="mt-2 ml-2 space-y-2 list-disc list-inside">
                  <li>Two-Factor Authentication (2FA) for added account security</li>
                  <li>Payment PINs for authorising deposits, withdrawals, and transfers</li>
                  <li>Push Notifications and OTPs to verify each transaction</li>
                  <li>Real-Time Monitoring to detect and prevent fraudulent activity</li>
                </ul>
              }
              title="Protected by Industry-Leading Security"
              variant="faded"
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="">
        <div className="container space-y-24 container-md">
          {/* Call to Action */}
          <div className="relative px-8 py-16 overflow-hidden bg-gradient-to-b from-primary/20 to-background rounded-t-3xl">
            <div className="space-y-12">
              <div className="relative z-10 space-y-4 text-center">
                <h2 className="font-bold text-primary title">Start Using PayStop Today</h2>
                <p className="max-w-3xl mx-auto text-foreground/70">
                  PayStop makes it easy to access your digital bank account or mobile wallet through
                  our trusted local Super Agents. Whether you’re depositing, withdrawing, or
                  transferring money, our platform is secure, fast, and simple to use.
                </p>
              </div>
              {/* <div className="flex justify-center gap-6">
                <Button color="primary" size="lg">
                  Get Started
                </Button>

                <Button
                  as={Link}
                  className="hover:bg-primary hover:text-white"
                  color="primary"
                  href="/agent-network-map"
                  size="lg"
                  variant="bordered"
                >
                  Find an Agent
                </Button>
              </div> */}

              {/* Contact Us */}
              <ContactUsCTA
                className="bg-background border-primary-100"
                description={'If you have any questions or need assistance, we’re here to help!'}
                email={'  support@paystop.io'}
                office={'23 Business Avenue, City, Country'}
                phone={'+237 676962723'}
                showOffice={false}
                title={'Get in Touch'}
              />

              <p className="max-w-3xl mx-auto text-sm text-center text-foreground/70">
                Connecting You to a World of Financial Services With PayStop, accessing your digital
                bank account or mobile wallet is simple and secure. We’re here to help you manage
                your financial transactions with ease and confidence.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Download />
    </>
  );
}
